import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  Users,
  ClipboardList,
  CreditCard,
  Inbox,
  ScrollText,
  Compass,
  LogOut,
  Shield,
  KeyRound,
  BarChart3,
  Activity,
  Palette,
  Plug,
  ShieldAlert,
  BadgePercent,
  ShieldCheck,
  Search,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import BrandLogo from "@/components/BrandLogo";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutGrid, end: true, testid: "admin-nav-overview" },
  { to: "/admin/users", label: "Users", icon: Users, testid: "admin-nav-users" },
  {
    to: "/admin/assessments",
    label: "Assessments",
    icon: ClipboardList,
    testid: "admin-nav-assessments",
  },
  {
    to: "/admin/subscriptions",
    label: "Subscriptions",
    icon: CreditCard,
    testid: "admin-nav-subscriptions",
  },
  {
    to: "/admin/reports",
    label: "Reporting",
    icon: BarChart3,
    testid: "admin-nav-reports",
  },
  { to: "/admin/contact", label: "Contact inbox", icon: Inbox, testid: "admin-nav-contact" },
  {
    to: "/admin/integrations",
    label: "Integrations",
    icon: KeyRound,
    testid: "admin-nav-integrations",
  },
  {
    to: "/admin/cover-pages",
    label: "Cover pages",
    icon: Palette,
    testid: "admin-nav-cover-pages",
  },
  {
    to: "/admin/api-keys",
    label: "API keys",
    icon: Plug,
    testid: "admin-nav-api-keys",
  },
  {
    to: "/admin/usage",
    label: "Spend Guard",
    icon: ShieldAlert,
    testid: "admin-nav-usage",
  },
  {
    to: "/admin/promo-codes",
    label: "Promo codes",
    icon: BadgePercent,
    testid: "admin-nav-promo-codes",
  },
  {
    to: "/admin/code-quality",
    label: "Code quality",
    icon: ShieldCheck,
    testid: "admin-nav-code-quality",
  },
  {
    to: "/admin/site-meta",
    label: "Site meta / SEO",
    icon: Search,
    testid: "admin-nav-site-meta",
  },
  {
    to: "/admin/jobs",
    label: "Background jobs",
    icon: Activity,
    testid: "admin-nav-jobs",
  },
  { to: "/admin/logs", label: "Audit log", icon: ScrollText, testid: "admin-nav-logs" },
];

export default function AdminLayout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    navigate("/", { replace: true });
    await logout();
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900" data-testid="admin-layout">
      <aside
        className="fixed inset-y-0 left-0 w-64 bg-slate-950 text-slate-100 hidden lg:flex flex-col"
        data-testid="admin-sidebar"
      >
        <Link
          to="/admin"
          className="px-6 py-5 flex items-center gap-2 border-b border-slate-800"
          data-testid="admin-brand-link"
        >
          <BrandLogo size="md" tone="dark" testid="admin-brand-logo" />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-medium">The AI Took My Job</span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
              Admin console
            </span>
          </div>
        </Link>

        <nav className="flex-1 px-3 py-5 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-white/10 text-white"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-slate-800 px-4 py-4 space-y-3">
          <div className="flex items-center gap-3" data-testid="admin-current-user">
            <div className="h-8 w-8 rounded-full bg-amber-400 text-slate-900 flex items-center justify-center text-sm font-semibold">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="leading-tight min-w-0">
              <div className="text-sm font-medium truncate">{user?.name || "Admin"}</div>
              <div className="text-xs text-slate-400 truncate flex items-center gap-1">
                <Shield className="h-3 w-3" /> {user?.email}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="w-full inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-medium text-slate-200 hover:bg-slate-800 transition-colors"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-3.5 w-3.5" /> Log out
          </button>
        </div>
      </aside>

      <main className="lg:pl-64">
        {/* Mobile top bar */}
        <div className="lg:hidden bg-slate-950 text-slate-100 px-5 py-4 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2" data-testid="admin-mobile-brand">
            <Compass className="h-4 w-4" />
            <span className="text-sm font-medium">Admin</span>
          </Link>
          <button
            onClick={onLogout}
            className="text-xs text-slate-300"
            data-testid="admin-mobile-logout"
          >
            Log out
          </button>
        </div>
        <div className="lg:hidden bg-slate-900 text-slate-100 overflow-x-auto">
          <div className="flex gap-1 px-3 py-2 min-w-max">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`mobile-${n.testid}`}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs whitespace-nowrap ${
                    isActive ? "bg-white text-slate-900" : "text-slate-300"
                  }`
                }
              >
                <n.icon className="h-3.5 w-3.5" /> {n.label}
              </NavLink>
            ))}
          </div>
        </div>

        <header className="px-6 lg:px-10 py-6 border-b border-slate-200 bg-white">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1
                className="text-2xl sm:text-3xl font-medium tracking-tight text-slate-900"
                data-testid="admin-page-title"
              >
                {title}
              </h1>
              {subtitle && (
                <p className="mt-1 text-sm text-slate-500" data-testid="admin-page-subtitle">
                  {subtitle}
                </p>
              )}
            </div>
            {actions && <div className="flex items-center gap-2">{actions}</div>}
          </div>
        </header>

        <div className="px-6 lg:px-10 py-8" data-testid="admin-page-content">
          {children}
        </div>
      </main>
    </div>
  );
}
